import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LoaderService } from '@icc/b2b/sales-document';
import { APP_VERSION, ClockService, Complaint, ComplaintAppointment, ComplaintAppointmentColumnKey, ComplaintAppointmentColumnSortOrder, ComplaintAppointmentFilters, complaintAppointmentListColumns, ComplaintAppointmentWithComplaintDetails, drawerAnimation, IccDocument, openedAppointmentStatuses, Synced } from '@icc/b2b/shared';
import { ConfigService, core, TranslateService } from '@icc/shared';
import { parseDate } from '@icc/shared/helpers';
import { IDBServiceBrowser } from 'libs/b2b/sales-document/src/lib/idb.service.browser';
import { ComplaintService } from '../complaint.service';
import { combineLatest, map, ReplaySubject, Subscription } from 'rxjs';
import { NgxPermissionsService } from 'ngx-permissions';
import { ComplaintPartyListItem } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ActivatedRoute, Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { format } from 'date-fns';

@Component({
    selector: 'icc-complaint-appointment-list',
    templateUrl: './complaint-appointment-list.component.html',
    styleUrls: ['./complaint-appointment-list.component.scss'],
    animations: [drawerAnimation],
})
export class ComplaintAppointmentListComponent implements OnInit, OnDestroy, AfterViewInit {
    showMap = false;
    showDebugSection = false;

    resetMapZoomAndCenter = false;

    static crumbsSubject = new ReplaySubject<Record<string, any>>();

    drawerAnimationState: 'fadeIn' | 'fadeOut' = 'fadeIn';
    @ViewChild('drawer', { static: false }) drawer: ElementRef;
    @ViewChild('drawerButton', { static: false, read: ElementRef }) drawerButton: ElementRef;

    localAppointments: ComplaintAppointmentWithComplaintDetails[] = [];

    remoteAppointments: ComplaintAppointmentWithComplaintDetails[] = [];
    allAppointments: ComplaintAppointmentWithComplaintDetails[] = [];
    filteredAppointments: ComplaintAppointmentWithComplaintDetails[] = [];
    orderedAppointments: ComplaintAppointmentWithComplaintDetails[] = [];

    synced = new Map<string, Synced>();

    filters: ComplaintAppointmentFilters;
    columnSortOptions: Partial<
        Record<
            ComplaintAppointmentColumnKey,
            {
                order: 'asc' | 'desc' | null;
                index: number;
            } | null
        >
    > = {};
    columnsSortOrder: ComplaintAppointmentColumnSortOrder = [];
    workersList: Record<number, string> | null = null;
    contractorsList: ComplaintPartyListItem[] | null = null;

    mode$ = this.route.data.pipe(map((data) => data.mode || 'index'));
    mode: 'selection' | 'index' = 'index';

    offline = false;

    @Input() appointmentsInPage = 20;
    totalItems = 0;

    page: number;

    subscriptions: Subscription[] = [];

    sortMethods: Record<ComplaintAppointmentColumnKey, (a: ComplaintAppointment, b: ComplaintAppointment) => number> = {
        date: (a: ComplaintAppointment, b: ComplaintAppointment): number => {
            const aDate = this.getOldestDate([a]);
            const bDate = this.getOldestDate([b]);
            if (aDate > bDate) {
                return 1;
            }
            if (aDate < bDate) {
                return -1;
            }
            return 0;
        },
        created: (a: ComplaintAppointment, b: ComplaintAppointment): number => {
            const aDate = this.getOldestCreatedDate([a]);
            const bDate = this.getOldestCreatedDate([b]);
            if (aDate > bDate) {
                return 1;
            }
            if (aDate < bDate) {
                return -1;
            }
            return 0;
        },
        primaryKey: (a: ComplaintAppointment, b: ComplaintAppointment): number => {
            if (a?.id > b?.id) {
                return 1;
            }
            if (b?.id > a?.id) {
                return -1;
            }
            return 0;
        }
    };

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        const width = event?.target?.innerWidth ? event.target.innerWidth : event;
        this.drawerAnimationState = width > 1200 ? 'fadeIn' : 'fadeOut';
    }

    get loaded() {
        return ClockService.getClock()?.timestamp != null;
    }

    companiesMap = new Map<number, string>();

    localAppointmentsResolve: () => void;
    loadedLocalAppointments: Promise<void>;

    mobileView = false;

    hasAccessTo = {
        manageComplaint: false,
        viewComplaintOrderNumber: false,
        viewAssignedDistributor: false,
        viewAssignedAuthorizedRetailer: false,
    };

    constructor(
        private loaderService: LoaderService,
        private idb: IDBServiceBrowser,
        private http: HttpClient,
        private configService: ConfigService,
        @Inject(APP_VERSION) private appVersion: string,
        private complaintService: ComplaintService,
        private permissionsService: NgxPermissionsService,
        private translateService: TranslateService,
        private route: ActivatedRoute,
        private router: Router,
        private breakpointObserver: BreakpointObserver,
        private ngZone: NgZone,
    ) {
        this.loadedLocalAppointments = new Promise((resolve) => {
            this.localAppointmentsResolve = resolve;
        });
        this.subscriptions.push(
            combineLatest([
                this.route.data,
                this.route.queryParams,
            ]).subscribe(([data, queryParams]) => {
                this.showMap = queryParams['origin'] === 'appointment-map';
                this.setListCrumbs();
                if (queryParams.first) {
                    this.router.navigate([], {
                        queryParams: {
                            first: null,
                        },
                        queryParamsHandling: 'merge',
                    });
                    this.filters = {
                        status: openedAppointmentStatuses.map(el => el.value) as ComplaintAppointmentFilters['status'],
                        assignedUserId: 0,
                        assignedCompanyId: this.configService.user?.user?.ActiveCompany ? +this.configService.user.user.ActiveCompany : 0,
                        dateFrom: null,
                        dateTo: null,
                    };
                    this.setColumnSortDefaults();
                } else {
                    this.loadConfiguration();
                }
            }),
            this.complaintService.isSynced$().subscribe(({synced}) => {
                this.synced = synced;
            }),
            this.permissionsService.permissions$.subscribe(() => {
                this.setWorkersList();
                this.updateAppointments(true);
                this.checkAccess();
            })
        )
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.mode$.subscribe((mode) => {
                this.mode = mode;
            }),
            this.breakpointObserver.observe(['(max-width: 1200px)']).subscribe((result) => {
                this.mobileView = result.matches;
                if (result.matches && this.drawerAnimationState === 'fadeIn') {
                    this.drawerAnimationState = 'fadeOut';
                }
            })
        );
        this.setWorkersList();
        this.showDebugSection = (window as any).isDebugMode();
        window.addEventListener('debugModeChanged', this.updateDebugMode);
    }

    ngAfterViewInit(): void {
        let top = document.getElementById('top');
        if (top !== null) {
            top.scrollIntoView();
            top = null;
        }
    }

    ngOnDestroy() {
      this.subscriptions.forEach((s) => s.unsubscribe());
        window.removeEventListener('debugModeChanged', this.updateDebugMode);
    }

    updateDebugMode = () => {
        this.showDebugSection = (window as any).isDebugMode();
    }

    setListCrumbs() {
        const crumbs = [];

        const label =
        (this.showMap ? this.translateService.instant('COMPLAINT_APPOINTMENT|Mapa wizyt') :
                       this.translateService.instant('COMPLAINT_APPOINTMENT|Lista wizyt'));

        crumbs.push({
            label,
            url: '/trade/complaint/list/appointments',
        })

        ComplaintAppointmentListComponent.crumbsSubject.next({
            level: 0,
            crumbs: null,
        });

        ComplaintAppointmentListComponent.crumbsSubject.next({
            level: 1,
            crumbs,
        });
    }

    switchOfflineMode(offline: boolean) {
        this.offline = offline;
        this.updateAppointments(true);
        this.saveConfiguration();
    }

    async updateAppointments(updateRemote = false) {
        if (this.offline) {
            await this.getLocalAppointmentsWithDetails();
            this.allAppointments = this.localAppointments;
            this.filteredAppointments = this.filterAppointments();
            this.orderedAppointments = this.sort();
            this.totalItems = this.filteredAppointments.length;
        } else {
            if (updateRemote) {
                await this.getRemoteAppointments();
            }
            this.filteredAppointments = this.remoteAppointments;
            this.orderedAppointments = this.remoteAppointments;
        }
    }

    async getLocalAppointments() {
        this.loaderService.setLoadingAfterTime();
        const allAppointments = await this.idb.getAllDb('appointments');
        this.localAppointments = allAppointments;
        this.loaderService.setLoaded();
    }

    async getLocalAppointmentsWithDetails() {
        this.loaderService.setLoadingAfterTime();
        const allAppointments: ComplaintAppointmentWithComplaintDetails[] = (await this.idb.getAllDb('appointments')).filter((app) => app.isDeleted !== true);

        const documentIds = allAppointments.map((el) => el.documentId);
        const relatedDocuments: IccDocument[] = await this.idb.getManyDb('docs', documentIds);

        const relatedComplaints = relatedDocuments.filter((doc) => doc.type === 'complaint') as Complaint[];

        const documentMap = new Map<string, Complaint>();
        relatedComplaints.forEach((doc) => documentMap.set(doc.uuid, doc));

        this.localAppointments = allAppointments.map((app) => {
            let companyName = '';

            if (app.assignedCompanyId !== null) {
                if (app.assignedCompanyId === documentMap.get(app.documentId)?.authorizedRetailer?.companyId || app.assignedCompanyId === documentMap.get(app.documentId)?.authorizedRetailerCompanyId) {
                    companyName = documentMap.get(app.documentId)?.authorizedRetailer?.companyName;
                } else if (app.assignedCompanyId === documentMap.get(app.documentId)?.distributor?.companyId) {
                    companyName = documentMap.get(app.documentId)?.distributor?.companyName;
                } else if (app.assignedCompanyId === documentMap.get(app.documentId)?.manufacturer?.companyId) {
                    companyName = documentMap.get(app.documentId)?.manufacturer?.companyName;
                }
            }

            return {
                ...app,
                documentNumber: core.parseJson(documentMap.get(app.documentId)?.documentNumber),
                clientName: documentMap.get(app.documentId)?.clientName,
                installationAddress: documentMap.get(app.documentId)?.installationAddress,
                locationSource: app.locationSource || documentMap.get(app.documentId)?.locationSource,
                locationPartialMatchConfirmed: app.locationPartialMatchConfirmed || documentMap.get(app.documentId)?.locationPartialMatchConfirmed,
                locationDescription: app.locationDescription || documentMap.get(app.documentId)?.locationDescription,
                latitude: app.latitude !== undefined ? app.latitude : documentMap.get(app.documentId)?.latitude,
                longitude: app.longitude !== undefined ? app.longitude : documentMap.get(app.documentId)?.longitude,
                companyName,
                distributorCompanyName: documentMap.get(app.documentId)?.distributor?.companyName,
                authorizedRetailerCompanyName:
                    documentMap.get(app.documentId)?.authorizedRetailer?.companyName,
                complaintOrderNumber: documentMap.get(app.documentId)?.complaintOrderNumber,
            }
        });

        this.loaderService.setLoaded();
    }

    async getRemoteAppointments() {
        this.loaderService.setLoadingAfterTime();
        await this.setContractorsList();
        const serverAppointments = await this.http.post<{
            appointments: ComplaintAppointmentWithComplaintDetails[],
            limit: number,
            offset: number,
            totalCount: number,
        }>(`${window.location.origin}/api/b2b/appointments`, {
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
            filters: this.filters || {},
            sort: this.columnsSortOrder || [],
            offset: this.showMap ? 0 : (this.page - 1) * this.appointmentsInPage,
            limit: this.showMap ? 200 : this.appointmentsInPage,
        }).toPromise();
        if (serverAppointments?.appointments) {
            this.remoteAppointments = serverAppointments.appointments.map(appointment => {
                appointment.created = parseDate(appointment.created);
                appointment.modified = parseDate(appointment.modified);
                if (appointment.date) {
                    appointment.date = parseDate(appointment.date);
                }
                if (appointment.deletedAt) {
                    appointment.deletedAt = parseDate(appointment.deletedAt);
                }
                if (appointment.assignedCompanyId && this.contractorsList) {
                    const contractor = this.contractorsList ? Object.values(this.contractorsList).find((el) => el.companyId === appointment.assignedCompanyId) : null;
                    if (contractor) {
                        appointment.companyName = contractor.companyName;
                    }
                }
                return appointment;

            });
            this.totalItems = serverAppointments.totalCount;
        }
        this.loaderService.setLoaded();
    }

    changeDrawerAnimationState() {
        this.drawerAnimationState = 'fadeIn';
    }

    closeDrawer() {
        this.drawerAnimationState = 'fadeOut';
    }

    getDrawerBtnIconName(): 'more_horizontal' | 'more_vertical' {
        return this.drawerAnimationState === 'fadeIn' ? 'more_horizontal' : 'more_vertical';
    }

    @HostListener('document:click', ['$event.target'])
    public onClick(targetElement) {
        if (this.drawerAnimationState === 'fadeIn' && this.mobileView) {
            const matCalendarElement = document.querySelector('.mat-calendar');
            const clickedInsideDrawer = this.drawer?.nativeElement && (
                targetElement && this.drawer?.nativeElement?.contains(targetElement) ||
                this.drawerButton?.nativeElement?.contains(targetElement) ||
                matCalendarElement && matCalendarElement.contains(targetElement)
            );
            if (!clickedInsideDrawer) {
                this.ngZone.run(() => {
                    this.closeDrawer();
                });
            }
        }
    }

    async setWorkersList() {
        if (
            !this.workersList
        ) {
            this.workersList = await this.complaintService.getWorkersList();
        }
    }

    async setContractorsList() {
        if (
            !this.contractorsList
        ) {
            this.contractorsList = await this.complaintService.getAllContractors();
        }
    }

    /* Filters */

    setFilters({ filters, reset }: { filters: ComplaintAppointmentFilters; reset: boolean }) {
        this.filters = filters;
        if (reset) {
            this.setColumnSortDefaults();
        }
        this.page = 1;
        this.updateAppointments(true);
        this.saveConfiguration();
        if (this.mobileView) {
            this.closeDrawer();
        }
    }

    filterAppointments(): ComplaintAppointment[] {
        let appointments = [...this.allAppointments];

        if (this.filters) {
            if (this.filters && appointments && appointments.length) {
                if (this.filters?.status) {
                    appointments = this.filterByStatus(appointments);
                }

                if (this.filters?.dateFrom) {
                    appointments = this.filterByDateFrom(appointments);
                }

                if (this.filters?.dateTo) {
                    appointments = this.filterByDateTo(appointments);
                }

                if (this.filters?.assignedUserId) {
                    appointments = this.filterByAssignedUserId(appointments);
                }

                if (this.filters?.assignedCompanyId) {
                    appointments = this.filterByAssignedCompanyId(appointments);
                }
            }
        }
        return appointments;
    }

    filterByStatus(appointments: ComplaintAppointment[]): ComplaintAppointment[] {
            if (Array.isArray(this.filters.status)) {
                return appointments.filter((appointment) => this.filters.status.includes(appointment.status));
            } else {
                return appointments.filter((appointment) => appointment.status === this.filters.status);
        }
    }

    filterByDateFrom(appointments: ComplaintAppointment[]): ComplaintAppointment[] {
        const date = parseDate(this.filters.dateFrom);
        if (date) {
            return appointments.filter((appointment) =>
                appointment.date ?
                    new Date(appointment.date).getTime() >= new Date(date).getTime()
                    : true
            );
        } else {
            return [];
        }
    }

    filterByDateTo(appointments: ComplaintAppointment[]): ComplaintAppointment[] {
        const date = parseDate(this.filters.dateTo);
        if (date) {
            return appointments.filter((appointment) =>
                appointment.date
                    ? new Date(appointment.date).getTime() <= new Date(date).getTime()
                    : true
            );
        } else {
            return [];
        }
    }

    filterByAssignedUserId(appointments: ComplaintAppointment[]): ComplaintAppointment[] {
        return appointments.filter((appointment) => appointment.assignedUserId === this.filters?.assignedUserId);
    }

    filterByAssignedCompanyId(appointments: ComplaintAppointment[]): ComplaintAppointment[] {
        return appointments.filter((appointment) => appointment.assignedCompanyId === this.filters?.assignedCompanyId);
    }

    /* Sorting */

    private setColumnSortDefaults() {
        this.columnsSortOrder = [
            { column: 'date', order: 'asc' }
        ];
        this.setColumnSortOptions();
        this.saveConfiguration();
    }

    private setColumnSortOptions() {
        complaintAppointmentListColumns.forEach((el) => {
            this.columnSortOptions[el] = null;
        });

        if (this.columnsSortOrder?.length) {
            this.columnsSortOrder.forEach((el, i) => {
                if (el.column) {
                    this.columnSortOptions[el.column] = {
                        index: i + 1,
                        order: el.order,
                    };
                }
            })
        }
    }

    orderBy(column: ComplaintAppointmentColumnKey) {
        const columnOrderIndex = this.columnsSortOrder.findIndex((el) => el.column === column);
        let columnOrder = null;
        if (columnOrderIndex > -1) {
            columnOrder = this.columnsSortOrder[columnOrderIndex];
            this.columnsSortOrder.splice(columnOrderIndex, 1);
        }
        this.columnsSortOrder.unshift({
            column,
            order: columnOrder?.order === 'asc' ? 'desc' : 'asc',
        });
        this.setColumnSortOptions();
        this.page = 1;
        this.updateAppointments(true);
        this.saveConfiguration();
    }

    sort(): ComplaintAppointment[] {
        let appointments = [...this.filteredAppointments];

        if (this.columnsSortOrder && appointments.length) {
            appointments = appointments.sort((a, b) => {
                let order = 0;
                let i = 0;

                while (order === 0 && i < this.columnsSortOrder.length) {
                    const column = this.columnsSortOrder[i];
                    if (typeof this.sortMethods[column.column] === 'function') {
                        order = this.sortMethods[column.column](a, b);
                        if (column.order === 'desc') {
                            order = -order;
                        }
                    }
                    i++;
                }

                if (order === 0) {
                    order = this.sortMethods['created'](a, b);
                }

                return order;
            });
        }

        return appointments;
    }

    saveConfiguration() {
        sessionStorage.setItem(
            `complaint-appointment-list-${
                this.configService?.user?.user?.id
            }`,
            core.stringJson({
                sort: this.columnsSortOrder,
                filters: this.filters,
                page: this.page,
                offline: this.offline,
            })
        );
    }

    loadConfiguration() {
        const { sort, filters, page, offline } = core.parseJson(
            sessionStorage.getItem(
                `complaint-appointment-list-${
                    this.configService?.user?.user?.id
                }`
            )
        );
        this.page = page ?? 1;

        if (typeof filters === 'object') {
            this.filters = filters;
        } else {
            this.filters = {
                status: openedAppointmentStatuses.map(el => el.value) as ComplaintAppointmentFilters['status'],
                assignedUserId: 0,
                assignedCompanyId: this.configService.user?.user?.ActiveCompany ? +this.configService.user.user.ActiveCompany : 0,
                dateFrom: null,
                dateTo: null,
            };
        }

        if (Array.isArray(sort)) {
            this.columnsSortOrder = sort;
            this.setColumnSortOptions();
        } else {
            this.setColumnSortDefaults();
        }

        if (typeof offline === 'boolean') {
            this.offline = offline;
        }
    }

    getOldestDate(appointments: ComplaintAppointment[]) {
        const getDateTime = (appointment: ComplaintAppointment): number => {
            const d = appointment.date instanceof Date ? appointment.date : new Date(appointment.date);
            const dateStr = format(d, 'yyyy-MM-dd');
            const timeStr = appointment.time ? appointment.time : '00:00';
            return new Date(`${dateStr}T${timeStr}:00`).getTime();
        };

        let oldestTime = getDateTime(appointments[0]);

        for (const appointment of appointments) {
            const t = getDateTime(appointment);
            if (t < oldestTime) {
                oldestTime = t;
            }
        }
        return oldestTime;
    }

    getOldestCreatedDate(appointments: ComplaintAppointment[]) {
        let oldestDate = new Date(appointments[0].created);

        for (const appointment of appointments) {
            if (new Date(appointment.created) < oldestDate) {
                oldestDate = new Date(appointment.created);
            }
        }
        return oldestDate;
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    onShowMap(event: boolean) {
        this.showMap = event;
        this.resetMapProperties();
        this.updateAppointments(true);
        this.setListCrumbs();

        if (this.mobileView) {
            this.closeDrawer();
        }
    }

    loadRelatedCompaniesIds(): number[] {
        const uniqueAssignedCompanyIds = Array.from(
            new Set(
                this.filteredAppointments
                    .map((appointment) => appointment.assignedCompanyId)
                    .filter(id => id !== null && id !== undefined)
            )
        );

        return uniqueAssignedCompanyIds;
    }

    onUpdateAppointments(event: boolean) {
        this.updateAppointments(event);
    }

    onSaveConfiguration() {
        this.saveConfiguration();
    }

    onChangeOrderBy(event: ComplaintAppointmentColumnKey) {
        this.orderBy(event);
    }

    onCurrentPageEmitter(event: number) {
        this.page = event;
    }

    resetMapProperties() {
        this.resetMapZoomAndCenter = true;
        sessionStorage.setItem(`complaints-appointments-map-zoom-${
            this.configService?.user?.user?.id
        }`, '7');
    }

    async checkAccess() {
        this.hasAccessTo.manageComplaint = await this.permissionsService.hasPermission('complaints-manage');
        this.hasAccessTo.viewComplaintOrderNumber = this.hasAccessTo.manageComplaint && this.configService.user?.user?.ActiveCompanyLicenseType === 'license';
        this.hasAccessTo.viewAssignedDistributor = this.getUserType() === 'manufacturer';
        this.hasAccessTo.viewAssignedAuthorizedRetailer =
            this.getUserType() === 'distributor';
    }
}
